/**
 * The native-grain rate grid (CLE-2470) — Ani's v0 steps 2 and 3.
 *
 * One row per priced line, in the contract's own vocabulary (revenue codes, DRGs, service lines),
 * grouped by service line, with methodology / rate / bundled / carve-out and the agent's confidence
 * side by side. This is the surface for reading a rate sheet: a 16-line outpatient grid is 16 rows you
 * can scan, not 16 stacked cards you have to page through.
 *
 * **The grid does not edit.** Expanding a row hands off to `ProposedModelingForm`, which stays the one
 * place a value is changed and re-validated against its Zod schema. Duplicating field editing into
 * table cells would mean two code paths writing the same construct with different validation, which is
 * exactly the drift the typed schema was introduced to end. So: grid to read and triage, form to fix.
 */

import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import React, { useState } from "react";
import styled from "styled-components";

import { IDraftRecommendation } from "../../../store/queries/contractModelDrafts";
import { confidenceSeverity } from "../utils";
import {
  IRateGridGroup,
  IRateGridRow,
  gridSummary,
  toGridGroups,
} from "./rateGridRows";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const GroupCard = styled.div`
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  overflow: hidden;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.9rem;
  background: var(--surface-100);
  border-bottom: 1px solid var(--surface-border);
  font-weight: 600;
`;

/* The grid scrolls inside its own container so a long code list can never push the page sideways. */
const Scroller = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--surface-border);
    vertical-align: middle;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    color: var(--text-color-secondary);
    font-size: 0.8rem;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr.expanded {
    background: var(--surface-50);
  }
`;

/* Codes are the one column allowed to be long; it wraps instead of stretching the table. */
const Codes = styled.span`
  font-family: var(--font-family-monospace, monospace);
  font-size: 0.8rem;
  white-space: normal;
  word-break: break-word;
  display: inline-block;
  max-width: 22rem;
`;

const Muted = styled.span`
  color: var(--text-color-secondary);
`;

const ExpandCell = styled.td`
  padding: 0 !important;
  background: var(--surface-50);
`;

const CODE_KIND_LABEL: Record<string, string> = {
  revenue: "Rev",
  drg: "DRG",
  cpt: "CPT",
  none: "",
};

/** Long code lists are truncated with an exact remainder — "and 12 more" is a checkable claim. */
const renderCodes = (row: IRateGridRow): React.ReactNode => {
  if (row.codes.length === 0) return <Muted>—</Muted>;
  const shown = row.codes.slice(0, 8);
  const rest = row.codes.length - shown.length;
  const prefix = CODE_KIND_LABEL[row.codeKind];
  return (
    <Codes>
      {prefix ? `${prefix} ` : ""}
      {shown.join(", ")}
      {rest > 0 ? <Muted> and {rest} more</Muted> : null}
    </Codes>
  );
};

const statusTag = (row: IRateGridRow): React.ReactNode => {
  if (row.needsInput) return <Tag severity="danger" value="Needs input" />;
  if (row.status === "accepted")
    return <Tag severity="success" value="Accepted" />;
  // A rejected row is deliberately the quietest thing in the column — it is settled, not a problem.
  if (row.status === "rejected") return <Muted>Rejected</Muted>;
  if (row.status === "overridden")
    return <Tag severity="warning" value="Overridden" />;
  return <Tag severity="info" value="Pending" />;
};

interface IRateGridProps {
  recs: IDraftRecommendation[];
  /** Renders the full editable card for a row — the wizard passes its existing RecommendationCard. */
  renderDetail: (rec: IDraftRecommendation) => React.ReactNode;
  /** Scrolled-to / auto-expanded row, so a deep link into a recommendation still lands. */
  focusRecId?: string | null;
}

function RateGrid({ recs, renderDetail, focusRecId }: IRateGridProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const byId = new Map(recs.map((r) => [r.id, r]));
  const groups: IRateGridGroup[] = toGridGroups(recs);
  const summary = gridSummary(groups);

  const isExpanded = (id: string): boolean =>
    expanded[id] ?? (focusRecId != null && focusRecId === id);

  const toggle = (id: string): void =>
    setExpanded((prev) => ({ ...prev, [id]: !isExpanded(id) }));

  if (recs.length === 0) return null;

  return (
    <Wrap>
      <div className="flex align-items-center gap-2 flex-wrap">
        <Tag
          severity="info"
          value={`${summary.lines} rate ${
            summary.lines === 1 ? "line" : "lines"
          }`}
        />
        {summary.carveOuts > 0 ? (
          <Tag severity="warning" value={`${summary.carveOuts} carve-outs`} />
        ) : null}
        {summary.bundled > 0 ? (
          <Tag severity="info" value={`${summary.bundled} bundled`} />
        ) : null}
      </div>

      {groups.map((group) => (
        <GroupCard key={group.serviceLine}>
          <GroupHeader>
            <i className="pi pi-tag" />
            <span>{group.serviceLine}</span>
            <Muted className="ml-auto text-sm">
              {group.rows.length} {group.rows.length === 1 ? "line" : "lines"}
            </Muted>
          </GroupHeader>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <th style={{ width: "2.5rem" }} aria-label="Expand" />
                  <th>Description</th>
                  <th>Codes</th>
                  <th>Methodology</th>
                  <th>Rate</th>
                  <th>Bundled</th>
                  <th>Carve-out</th>
                  <th>AI</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => {
                  const rec = byId.get(row.id);
                  const open = isExpanded(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <tr className={open ? "expanded" : undefined}>
                        <td>
                          <Button
                            type="button"
                            className="p-button-text p-button-sm p-0"
                            aria-label={open ? "Collapse" : "Expand"}
                            icon={
                              open
                                ? "pi pi-chevron-down"
                                : "pi pi-chevron-right"
                            }
                            onClick={() => toggle(row.id)}
                          />
                        </td>
                        <td>{row.description}</td>
                        <td>{renderCodes(row)}</td>
                        <td>{row.methodology}</td>
                        <td>{row.rate || <Muted>—</Muted>}</td>
                        <td>
                          {row.bundled ? (
                            <i className="pi pi-check" aria-label="Bundled" />
                          ) : (
                            <Muted>—</Muted>
                          )}
                        </td>
                        <td>
                          {row.carveOut ? (
                            <i className="pi pi-check" aria-label="Carve-out" />
                          ) : (
                            <Muted>—</Muted>
                          )}
                        </td>
                        <td>
                          {row.confidence ? (
                            <Tag
                              severity={confidenceSeverity(row.confidence)}
                              value={row.confidence}
                            />
                          ) : (
                            <Muted>—</Muted>
                          )}
                        </td>
                        <td>{statusTag(row)}</td>
                      </tr>
                      {open && rec ? (
                        <tr>
                          <ExpandCell colSpan={9}>
                            {renderDetail(rec)}
                          </ExpandCell>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
        </GroupCard>
      ))}
    </Wrap>
  );
}

export default RateGrid;
